import { LOCALES, type Locale } from '@/lib/i18n/dict'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * i18n locale state — Step 7 경량 scaffold.
 *
 * - 기본값: 브라우저 언어에서 감지 (`navigator.language`)
 * - localStorage 영속화 (`robotai-locale`)
 * - `document.documentElement.lang` 에 현재 언어 반영 (a11y)
 */

interface I18nState {
  locale: Locale
  setLocale: (locale: Locale) => void
  toggle: () => void
}

function detectInitialLocale(): Locale {
  if (typeof navigator === 'undefined') return 'ko'
  const raw = navigator.language?.toLowerCase() ?? 'ko'
  if (raw.startsWith('ko')) return 'ko'
  if (raw.startsWith('en')) return 'en'
  return 'ko'
}

function applyToDocument(locale: Locale): void {
  if (typeof document === 'undefined') return
  document.documentElement.lang = locale
}

export const useI18nStore = create<I18nState>()(
  persist(
    (set, get) => ({
      locale: detectInitialLocale(),
      setLocale: (locale) => {
        applyToDocument(locale)
        set({ locale })
      },
      toggle: () => {
        const current = get().locale
        const idx = LOCALES.indexOf(current)
        const next = LOCALES[(idx + 1) % LOCALES.length]!
        applyToDocument(next)
        set({ locale: next })
      },
    }),
    {
      name: 'robotai-locale',
      onRehydrateStorage: () => (state) => {
        if (state) applyToDocument(state.locale)
      },
    },
  ),
)

/**
 * 앱 초기 로드 시점에 1 회 호출하여 current locale 을 `document.documentElement.lang`
 * 에 적용. `main.tsx` 에서 `initI18n()` 호출로 명시화.
 */
export function initI18n(): void {
  applyToDocument(useI18nStore.getState().locale)
}
